import axios from 'axios'
import { ZodError } from 'zod'

import type { CalculationDomainError } from '@/shared/types/cart'

export class ApiError extends Error {
	readonly status: number | null
	readonly code: string
	readonly raw: unknown
	readonly friendlyError: string | null

	constructor(
		message: string,
		status: number | null,
		code: string,
		raw: unknown,
		friendlyError: string | null = null,
	) {
		super(message)
		this.name = 'ApiError'
		this.status = status
		this.code = code
		this.raw = raw
		this.friendlyError = friendlyError
	}
}

// Thrown by calculateCart() when the API returns a structured domain errors array.
// Carries the full errors[] so the calculate thunk can inspect every error code
// (ITMNA, ORDCOUPINV, ITMOOSTK etc.) rather than only the first one.
export class CartApiError extends ApiError {
	readonly errors: CalculationDomainError[]

	constructor(errors: CalculationDomainError[], status: number | null, raw: unknown) {
		super('Cart calculation failed', status, 'CART_API_ERROR', raw)
		this.name = 'CartApiError'
		this.errors = errors
	}
}

export function normalizeZodError(error: ZodError): ApiError {
	return new ApiError(error.message, null, 'SCHEMA_ERROR', error)
}

export function normalizeError(error: unknown): ApiError {
	if (axios.isAxiosError(error)) {
		const status = error.response?.status ?? null
		// Guard against malformed responses (HTML error pages, plain strings, null, etc.)
		const responseData =
			typeof error.response?.data === 'object' && error.response.data !== null
				? (error.response.data as Record<string, unknown>)
				: undefined
		// Backend wraps errors in an `errors` array; read the first entry when present.
		const errorsArray = responseData?.errors
		const firstError =
			Array.isArray(errorsArray) && errorsArray.length > 0
				? (errorsArray[0] as Record<string, unknown>)
				: undefined
		const message = typeof firstError?.message === 'string' ? firstError.message : error.message
		// Prefer the backend domain code from the response body; fall back to the Axios transport code.
		const code =
			typeof firstError?.code === 'string' ? firstError.code : (error.code ?? 'UNKNOWN')
		const friendlyError =
			typeof firstError?.friendlyError === 'string' ? firstError.friendlyError : null

		return new ApiError(message, status, code, error, friendlyError)
	}

	if (error instanceof Error) {
		return new ApiError(error.message, null, 'UNKNOWN', error)
	}

	return new ApiError('An unexpected error occurred', null, 'UNKNOWN', error)
}
